import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ChevronRight, Moon, Clock, Building2, ChevronDown } from 'lucide-react';
import { getTodayPrayerTimes, getGlobalTimesToday } from '../api';
import CountdownTimer from '../components/CountdownTimer';

const PRAYER_KEYS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
const PRAYER_LABELS = {
  fajr: 'Fajr', sunrise: 'Sunrise', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha',
};
const PRAYER_ICONS = {
  fajr: '🌙', sunrise: '🌅', dhuhr: '☀️', asr: '🌤', maghrib: '🌇', isha: '🌃',
};

const PRAYER_STYLES = {
  fajr:    { color: 'from-indigo-900 to-navy-900',     accent: 'text-indigo-400',  border: 'border-indigo-500/30' },
  sunrise: { color: 'from-orange-900/60 to-navy-900',  accent: 'text-orange-300',  border: 'border-orange-500/20' },
  dhuhr:   { color: 'from-amber-900/50 to-navy-900',   accent: 'text-amber-400',   border: 'border-amber-500/30' },
  asr:     { color: 'from-orange-900/50 to-navy-900',  accent: 'text-orange-400',  border: 'border-orange-500/30' },
  maghrib: { color: 'from-pink-900/50 to-navy-900',    accent: 'text-pink-400',    border: 'border-pink-500/30' },
  isha:    { color: 'from-purple-900/50 to-navy-900',  accent: 'text-purple-400',  border: 'border-purple-500/30' },
};

function StatusBadge({ status, label }) {
  if (!status) return null;
  const styles = {
    now:      'bg-emerald-500/20 border-emerald-500/40 text-emerald-400',
    upcoming: 'bg-amber-500/20 border-amber-500/40 text-amber-400',
    happened: 'bg-red-500/10 border-red-500/20 text-red-500',
  };
  const dots = {
    now:      'bg-emerald-400 animate-pulse',
    upcoming: 'bg-amber-400',
    happened: 'bg-red-500',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${styles[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full inline-block ${dots[status]}`} />
      {label}
    </span>
  );
}

function CurrentSalahCard({ prayerKey, prayerData, isCurrent }) {
  const [expanded, setExpanded] = useState(true);
  const style = PRAYER_STYLES[prayerKey] || PRAYER_STYLES.isha;
  const timesAvailable = prayerData.filter((m) => m[prayerKey]);
  const earliest = timesAvailable.length > 0 ? timesAvailable.map((m) => m[prayerKey]).sort()[0] : null;

  // Determine if any mosque has started this prayer (for header badge)
  const anyStarted = timesAvailable.some((m) => {
    const s = getTimeStatus(m[prayerKey]);
    return s?.status === 'now' || s?.status === 'happened';
  });
  const headerStatus = isCurrent ? (anyStarted ? 'now' : 'upcoming') : null;
  const headerLabel = headerStatus === 'now' ? 'Now' : 'Next Salah';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      className={`rounded-2xl overflow-hidden border ${isCurrent ? 'border-emerald-500/60' : style.border} shadow-lg`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full bg-gradient-to-r ${style.color} p-5 flex items-center justify-between text-left hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-4">
          <div className="text-3xl">{PRAYER_ICONS[prayerKey]}</div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-bold text-xl">{PRAYER_LABELS[prayerKey]}</h3>
              {isCurrent && <StatusBadge status={headerStatus} label={headerLabel} />}
            </div>
            {earliest ? (
              <p className="text-sm text-gray-300 mt-0.5">
                Earliest: <span className={`font-semibold ${style.accent}`}>{earliest}</span>
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-0.5">No times available</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm ${style.accent}`}>
            {timesAvailable.length} mosque{timesAvailable.length !== 1 ? 's' : ''}
          </span>
          <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-navy-900/80 border-t border-white/5">
              {prayerData.length === 0 ? (
                <div className="p-6 text-center text-gray-400">No data available for today.</div>
              ) : (
                <div className="divide-y divide-white/5">
                  {prayerData.map((entry) => {
                    const mosque = entry.mosqueId;
                    const timeStr = entry[prayerKey];
                    const ts = getTimeStatus(timeStr);
                    const happened = ts?.status === 'happened';
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((mosque?.address || '') + ', Oldham')}`;

                    return (
                      <div
                        key={entry._id}
                        className={`px-4 py-3 flex items-center justify-between gap-4 transition-opacity ${happened ? 'opacity-40' : ''}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link
                              to={`/mosque/${mosque?._id}`}
                              className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors"
                            >
                              <Building2 className="w-3.5 h-3.5 text-emerald-500/50 flex-shrink-0" />
                              <span className="font-medium text-white text-sm">{mosque?.name}</span>
                            </Link>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-gray-500 hover:text-emerald-400 transition-colors text-xs"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate max-w-[180px]">{mosque?.address}</span>
                            </a>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {timeStr && (
                            <span className={`font-bold text-base ${happened ? 'text-gray-500' : style.accent}`}>
                              {timeStr}
                            </span>
                          )}
                          {ts && <StatusBadge status={ts.status} label={ts.label} />}
                          {!timeStr && <span className="text-gray-600 text-sm">N/A</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const GRACE_PERIOD_MINUTES = 10;

// Returns status of a specific mosque's prayer time
function getTimeStatus(timeStr) {
  if (!timeStr) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [h, m] = timeStr.split(':').map(Number);
  const t = new Date(today);
  t.setHours(h, m, 0, 0);
  const diff = t - now; // positive = future

  if (diff > 0) {
    const totalMins = Math.round(diff / 60000);
    const days = Math.floor(totalMins / 1440);
    const hrs = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    let label = 'in ';
    if (days > 0) label += `${days}d `;
    if (hrs > 0) label += `${hrs}hr `;
    if (mins > 0 || (days === 0 && hrs === 0)) label += `${mins}min`;
    return { status: 'upcoming', label: label.trim() };
  }
  const elapsed = -diff;
  if (elapsed <= GRACE_PERIOD_MINUTES * 60000) {
    return { status: 'now', label: 'Now' };
  }
  return { status: 'happened', label: 'happened' };
}

// Returns the latest time across all mosques for each prayer
function getLatestTimes(prayerData) {
  const result = {};
  ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'].forEach((key) => {
    const times = prayerData.map((m) => m[key]).filter(Boolean);
    if (times.length > 0) result[key] = times.sort().pop();
  });
  return result;
}

// Returns which prayers should still be visible based on latest mosque times
function getVisiblePrayers(prayerData) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const latest = getLatestTimes(prayerData);
  const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

  const visible = prayers.filter((key) => {
    if (!latest[key]) return true;
    const [h, m] = latest[key].split(':').map(Number);
    const t = new Date(today);
    t.setHours(h, m, 0, 0);
    return t > now;
  });

  // All prayers done for the day — show all (reset for tomorrow)
  return visible.length > 0 ? visible : prayers;
}

function getCurrentOrNextPrayer(prayerTimes) {
  if (!prayerTimes) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

  const prayerList = prayers
    .filter((p) => prayerTimes[p])
    .map((p) => {
      const [h, m] = prayerTimes[p].split(':').map(Number);
      const t = new Date(today);
      t.setHours(h, m, 0, 0);
      return { key: p, time: t };
    });

  // Find the most recent prayer that has already started
  const passed = prayerList.filter((p) => p.time <= now);
  if (passed.length > 0) {
    const last = passed[passed.length - 1];
    const minutesSince = (now - last.time) / 60000;
    if (minutesSince <= GRACE_PERIOD_MINUTES) {
      // Within grace period — keep showing this prayer
      return last.key;
    }
  }

  // Show the next upcoming prayer
  const next = prayerList.find((p) => p.time > now);
  return next ? next.key : 'fajr';
}

function getEarliestTimes(timesArray) {
  if (!timesArray || timesArray.length === 0) return null;
  const result = {};
  PRAYER_KEYS.forEach((key) => {
    const valid = timesArray.map((t) => t[key]).filter(Boolean);
    if (valid.length > 0) {
      result[key] = valid.sort()[0];
    }
  });
  return result;
}

// Islamic geometric star shape
function GeometricStar({ size = 40, color = '#10b981', opacity = 0.15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ opacity }}>
      <polygon
        points="50,5 61,35 95,35 68,57 79,91 50,70 21,91 32,57 5,35 39,35"
        fill={color}
      />
    </svg>
  );
}

export default function Home() {
  const [prayerData, setPrayerData] = useState([]);
  const [globalTimes, setGlobalTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());

  const today = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    Promise.all([
      getTodayPrayerTimes(),
      getGlobalTimesToday(),
    ])
      .then(([mosqueRes, globalRes]) => {
        setPrayerData(mosqueRes.data);
        setGlobalTimes(globalRes.data);
      })
      .catch(() => setError('Failed to load prayer times'))
      .finally(() => setLoading(false));
  }, []);

  // Re-evaluate current prayer every minute
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Use global times for countdown if set, otherwise fall back to earliest mosque times
  const standardTimes = globalTimes || getEarliestTimes(prayerData);
  const nextPrayerKey = getCurrentOrNextPrayer(standardTimes);
  const visiblePrayers = getVisiblePrayers(prayerData);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-bg relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Decorative geometric elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `
                linear-gradient(rgba(16, 185, 129, 0.5) 1px, transparent 1px),
                linear-gradient(90deg, rgba(16, 185, 129, 0.5) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }}
          />

          {/* Stars */}
          <div className="absolute top-20 left-10"><GeometricStar size={60} color="#10b981" opacity={0.12} /></div>
          <div className="absolute top-40 right-20"><GeometricStar size={80} color="#f59e0b" opacity={0.1} /></div>
          <div className="absolute bottom-40 left-20"><GeometricStar size={50} color="#10b981" opacity={0.08} /></div>
          <div className="absolute bottom-20 right-10"><GeometricStar size={70} color="#f59e0b" opacity={0.1} /></div>
          <div className="absolute top-1/2 left-5"><GeometricStar size={40} color="#10b981" opacity={0.06} /></div>
          <div className="absolute top-1/3 right-5"><GeometricStar size={45} color="#f59e0b" opacity={0.08} /></div>

          {/* Large decorative circles */}
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-emerald-500/10" />
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full border border-emerald-500/5" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full border border-gold-500/10" />

          {/* Crescent moon decoration */}
          <div className="absolute top-24 right-1/4 float-animation opacity-20">
            <Moon className="w-16 h-16 text-gold-400" />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          {/* Arabic greeting */}
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-gold-400 font-arabic text-2xl mb-4"
            style={{ direction: 'rtl' }}
          >
            السَّلَامُ عَلَيْكُمْ
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="text-5xl sm:text-7xl font-bold mb-4 leading-tight">
              <span className="gradient-text">Oldham</span>
              <br />
              <span className="text-white">Prayer Times</span>
            </h1>
            <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto mb-2">
              Accurate salah times for all mosques in Oldham, Greater Manchester
            </p>
            <p className="text-emerald-400 text-sm">{today}</p>
          </motion.div>

          {/* Countdown Timer */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 inline-block"
          >
            <div className="glass-card-light rounded-2xl p-8 glow-emerald">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-emerald-400 text-sm font-medium uppercase tracking-wider">Next Prayer Countdown</p>
              </div>
              {loading ? (
                <div className="flex items-center justify-center gap-3 py-4">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-gray-400">Loading times...</span>
                </div>
              ) : (
                <CountdownTimer prayerTimes={standardTimes} />
              )}
            </div>
          </motion.div>

          {/* Today's standard times */}
          {!loading && standardTimes && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-8 grid grid-cols-3 sm:grid-cols-6 gap-3 max-w-3xl mx-auto"
            >
              {PRAYER_KEYS.map((key) => (
                <div
                  key={key}
                  className={`rounded-xl p-3 text-center transition-all ${
                    key === nextPrayerKey
                      ? 'bg-emerald-500/20 border border-emerald-500/50 glow-emerald'
                      : 'glass-card'
                  }`}
                >
                  <div className="text-lg mb-1">{PRAYER_ICONS[key]}</div>
                  <div className={`text-xs font-medium mb-1 ${key === nextPrayerKey ? 'text-emerald-400' : 'text-gray-400'}`}>
                    {PRAYER_LABELS[key]}
                  </div>
                  <div className="text-white font-semibold text-sm">
                    {standardTimes[key] || 'N/A'}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10 flex flex-wrap justify-center gap-4"
          >
            <Link to="/prayers-by-date" className="btn-primary px-6 py-3 rounded-xl font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Full Prayer Timetable
            </Link>
            <Link to="/mosques" className="glass-card-light px-6 py-3 rounded-xl font-semibold text-white flex items-center gap-2 hover:border-emerald-500/40 transition-all">
              <Building2 className="w-5 h-5" />
              All Mosques
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-emerald-500/40 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-emerald-400 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Current Salah Section */}
      <section className="section-bg py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              <span className="gradient-text">Remaining</span>
              <span className="text-white"> Jammat Times</span>
            </h2>
            <p className="text-gray-400">
              Today's remaining salah jammat times across all Oldham mosques
            </p>
          </motion.div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => <div key={i} className="rounded-2xl h-20 shimmer" />)}
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-400">{error}</p>
              <p className="text-gray-500 text-sm mt-2">Make sure the backend server is running.</p>
            </div>
          ) : (
            <AnimatePresence>
              <div className="space-y-4">
                {visiblePrayers.map((key, i) => (
                  <CurrentSalahCard
                    key={key}
                    prayerKey={key}
                    prayerData={prayerData}
                    isCurrent={key === nextPrayerKey}
                  />
                ))}
              </div>
            </AnimatePresence>
          )}

          <div className="text-center mt-8">
            <Link to="/prayers-by-date" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
              View Full Timetable
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: '🕌', label: '7 Mosques', desc: 'All Oldham mosques' },
              { icon: '🕐', label: 'Live Times', desc: 'Updated daily' },
              { icon: '📅', label: 'Timetables', desc: '30-day schedule' },
              { icon: '🕋', label: "Jumu'ah", desc: 'Friday prayer times' },
            ].map(({ icon, label, desc }) => (
              <div key={label} className="text-center glass-card rounded-xl p-5">
                <div className="text-3xl mb-2">{icon}</div>
                <div className="text-white font-semibold">{label}</div>
                <div className="text-gray-400 text-xs mt-1">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
